import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase, type Creator } from "@/lib/supabaseClient";

export default function FeaturedCreatorsSection() {
  const [creators, setCreators] = useState<Creator[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchFeatured = async () => {
      setLoading(true);
      const { data, error } = await supabase
        .from("creators")
        .select("*")
        .eq("approved", true)
        .eq("featured", true)
        .order("rating", { ascending: false })
        .limit(3);
      if (!error && data) setCreators(data as Creator[]);
      setLoading(false);
    };
    fetchFeatured();
  }, []);

  const tags = (creator: Creator) =>
    (creator.categories || "")
      .split(",")
      .map((t) => t.trim())
      .filter(Boolean);

  return (
    <section className="py-20 bg-[#faf8f4]">
      <div className="max-w-7xl mx-auto px-6">
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 mb-12">
          <div>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-[#f5ede0] text-[#8b5e3c] text-xs font-semibold uppercase tracking-widest mb-4">
              <i className="ri-sparkling-line"></i>
              Featured Creators
            </div>
            <h2
              className="text-4xl font-bold text-[#2c1e0f]"
              style={{ fontFamily: "'Playfair Display', serif" }}
            >
              Meet Our Talented Makers
            </h2>
            <p className="text-[#7a6555] mt-2 max-w-lg">
              Handpicked creators bringing exceptional craftsmanship to the junk journal and snail
              mail community.
            </p>
          </div>
          <Link
            to="/featured"
            className="flex items-center gap-2 text-sm font-medium text-[#8b5e3c] hover:text-[#7a5234] transition-colors whitespace-nowrap"
          >
            View all creators
            <div className="w-5 h-5 flex items-center justify-center">
              <i className="ri-arrow-right-line"></i>
            </div>
          </Link>
        </div>

        {/* Creator Cards */}
        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {Array.from({ length: 3 }).map((_, i) => (
              <div
                key={i}
                className="rounded-2xl overflow-hidden bg-white border border-[#ede5d8] animate-pulse"
              >
                <div className="h-56 bg-[#f0e8dc]"></div>
                <div className="p-5 space-y-3">
                  <div className="h-4 bg-[#f0e8dc] rounded w-3/4"></div>
                  <div className="h-3 bg-[#f0e8dc] rounded w-full"></div>
                  <div className="h-3 bg-[#f0e8dc] rounded w-2/3"></div>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {creators.map((creator, idx) => (
              <div
                key={creator.id}
                className={`rounded-2xl overflow-hidden bg-white border border-[#ede5d8] hover:-translate-y-1 transition-transform duration-300 cursor-pointer ${
                  idx === 0 ? "ring-2 ring-[#c9a87c]/40" : ""
                }`}
              >
                {/* Image */}
                <div className="relative h-56 overflow-hidden bg-[#f5ede0]">
                  {creator.image_url ? (
                    <img
                      src={creator.image_url}
                      alt={creator.business_name}
                      className="w-full h-full object-cover object-top"
                    />
                  ) : (
                    <div className="w-full h-full flex items-center justify-center text-[#c9a87c]">
                      <i className="ri-image-line text-4xl"></i>
                    </div>
                  )}
                  {idx === 0 && (
                    <div className="absolute top-3 left-3 px-2.5 py-1 rounded-full bg-[#8b5e3c] text-white text-xs font-semibold">
                      Top Creator
                    </div>
                  )}
                  {creator.type && (
                    <div className="absolute top-3 right-3 px-2.5 py-1 rounded-full bg-white/90 text-[#3a2e1e] text-xs font-medium">
                      {creator.type}
                    </div>
                  )}
                </div>

                {/* Content */}
                <div className="p-5">
                  <div className="flex items-start justify-between mb-2">
                    <h3
                      className="font-bold text-[#2c1e0f] text-lg"
                      style={{ fontFamily: "'Playfair Display', serif" }}
                    >
                      {creator.business_name}
                    </h3>
                    {creator.rating > 0 && (
                      <div className="flex items-center gap-1 text-[#c9a87c]">
                        <div className="w-4 h-4 flex items-center justify-center">
                          <i className="ri-star-fill text-sm"></i>
                        </div>
                        <span className="text-xs font-semibold text-[#3a2e1e]">
                          {creator.rating}
                        </span>
                      </div>
                    )}
                  </div>
                  {creator.location && (
                    <p className="text-xs text-[#8b7355] mb-3 flex items-center gap-1">
                      <i className="ri-map-pin-line"></i>
                      {creator.location}
                    </p>
                  )}
                  {creator.bio && (
                    <p className="text-sm text-[#6b5a4a] leading-relaxed mb-4 line-clamp-2">
                      {creator.bio}
                    </p>
                  )}

                  {/* Tags */}
                  {tags(creator).length > 0 && (
                    <div className="flex flex-wrap gap-1.5 mb-4">
                      {tags(creator).map((tag) => (
                        <span
                          key={tag}
                          className="px-2.5 py-0.5 rounded-full bg-[#f5ede0] text-[#8b5e3c] text-xs font-medium"
                        >
                          {tag}
                        </span>
                      ))}
                    </div>
                  )}

                  <div className="flex items-center justify-between">
                    {creator.product_count > 0 && (
                      <span className="text-xs text-[#9a8a78]">
                        {creator.product_count} products
                      </span>
                    )}
                    <Link
                      to="/featured"
                      className="px-4 py-1.5 rounded-full border border-[#c9a87c] text-[#8b5e3c] text-xs font-semibold hover:bg-[#f5ede0] transition-colors whitespace-nowrap ml-auto"
                    >
                      View Collection
                    </Link>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </section>
  );
}
